import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, formatZAR } from "@/lib/api";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/orders/track/${orderNumber}`).then((r) => setOrder(r.data)).catch((e) => setErr(e));
  }, [orderNumber]);

  if (err) return <div className="max-w-2xl mx-auto px-6 py-20 text-center"><p>Order not found.</p></div>;
  if (!order) return <div className="max-w-2xl mx-auto px-6 py-20 text-center small-label">Loading…</div>;

  return (
    <div data-testid="order-confirmation-page" className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-center">
        <CheckCircle2 strokeWidth={1.25} className="w-12 h-12 mx-auto text-gold" />
        <p className="small-label mt-4">Order confirmed</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3">Thank you, {order.customer.first_name}.</h1>
        <p className="text-stone mt-3">A confirmation has been sent to {order.customer.email}.</p>
        <p className="mt-2 small-label text-charcoal">Order # <span data-testid="order-number">{order.order_number}</span></p>
      </div>

      <div className="mt-12 bg-nude rounded-3xl p-8">
        <p className="small-label">Items</p>
        <div className="mt-4 space-y-4">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex gap-4 items-center">
              <img src={i.image} alt={i.title} className="w-16 h-20 object-cover rounded-xl bg-ivory" />
              <div className="flex-1">
                <p className="font-medium text-sm">{i.title}</p>
                <p className="small-label">{i.length}″ · {i.density} · Qty {i.quantity}</p>
              </div>
              <p className="text-sm">{formatZAR(i.line_total)}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 border-t border-stone-line pt-5 space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{formatZAR(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Shipping ({order.shipping_method})</span><span>{order.shipping_cost === 0 ? "Free" : formatZAR(order.shipping_cost)}</span></div>
          <div className="flex justify-between font-medium text-base"><span>Total paid</span><span>{formatZAR(order.total)}</span></div>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-6">
        <div className="border border-stone-line rounded-3xl p-6">
          <p className="small-label">Shipping to</p>
          <p className="mt-2 text-sm leading-relaxed">
            {order.customer.first_name} {order.customer.last_name}<br />
            {order.customer.address_line1}<br />
            {order.customer.address_line2 && <>{order.customer.address_line2}<br /></>}
            {order.customer.city}, {order.customer.province} {order.customer.postal_code}<br />
            {order.customer.country}
          </p>
        </div>
        <div className="border border-stone-line rounded-3xl p-6">
          <p className="small-label">Payment</p>
          <p className="mt-2 text-sm capitalize">{order.payment_method.replace("_", " ")} · {order.payment_status}</p>
          <p className="small-label mt-4">Status</p>
          <p className="mt-2 text-sm capitalize">{order.fulfillment_status}</p>
        </div>
      </div>

      <div className="text-center mt-12">
        <Link to="/shop" data-testid="continue-shopping-link" className="btn-outline">Continue shopping</Link>
      </div>
    </div>
  );
}
